import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getOrCreateDirectConversation } from "../firebase/messagingUtils";
import {
  startIncomingCallAlert,
  stopIncomingCallAlert,
} from "../utils/incomingCallAlert";
import { getAndroidAudioState } from "../utils/androidAudioState";
import "./IncomingCallOverlay.css";
import { useCallSession } from "../context/CallSessionContext";

/**
 * Global incoming call handler so users see offers anywhere in the app.
 * Leans on the existing DirectChat call engine for accept/decline.
 */
export default function IncomingCallManager({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const callSession = useCallSession();
  const receiveIncomingCall =
    callSession?.actions?.setIncomingCallData || null;
  const callState = callSession?.state?.callState;
  const inCall = callSession?.state?.inCall;
  const incomingCallId = callSession?.state?.incomingCallId;
  const appVisibility = callSession?.state?.appVisibility;
  const [activeCall, setActiveCall] = useState(null);
  const lastNotificationIdRef = useRef(null);
  const timeoutRef = useRef(null);
  const notificationPermissionsCheckedRef = useRef(false);
  const audioDecisionRef = useRef(null);
  const audioDecisionCallIdRef = useRef(null);
  const readyMissingLoggedRef = useRef(false);
  const ringingWriteRef = useRef(new Set());
  const lastHydratedRef = useRef({ id: null, status: null });
  const suppressOverlay = location.pathname.startsWith("/direct/");
  const replaceCallNavigation =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

  // Listen for ringing calls for the current user
  useEffect(() => {
    if (!currentUser?.uid) return;

    const q = query(
      collection(db, "calls"),
      where("to", "==", currentUser.uid),
      where("status", "in", ["calling", "ringing"])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ringingCalls = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      // Most recent first (createdAt may be missing on some docs)
      const sorted = ringingCalls.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      const top = sorted[0] || null;
      setActiveCall((prev) => {
        if (!top) return prev ? null : prev;
        if (prev && prev.id === top.id && prev.status === top.status) {
          return prev;
        }
        return top;
      });

      if (!receiveIncomingCall) return;

      if (top) {
        // Do not hydrate a second, different incoming call while the engine is already handling one
        const engineBusy =
          (callState && callState !== "idle") || inCall;

        if (engineBusy && incomingCallId !== top.id) {
          if (!readyMissingLoggedRef.current) {
            console.log(
              "[IncomingCallManager] engine busy; skipping incoming call hydration (callState=" +
              callState +
              ")"
            );
            readyMissingLoggedRef.current = true;
          }
          return;
        }

        if (
          (callState === "connecting" || callState === "connected") ||
          inCall
        ) {
          return;
        }

        if (
          lastHydratedRef.current.id === top.id &&
          lastHydratedRef.current.status === top.status
        ) {
          return;
        }
        lastHydratedRef.current = { id: top.id, status: top.status };
        receiveIncomingCall(top);
        readyMissingLoggedRef.current = false;
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid, receiveIncomingCall, callState]);

  // Android: promote "calling" to "ringing" once the callee sees it.
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
      return;
    }

    const q = query(
      collection(db, "calls"),
      where("to", "==", currentUser.uid),
      where("status", "==", "calling")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docs.forEach((docSnap) => {
        if (ringingWriteRef.current.has(docSnap.id)) return;
        ringingWriteRef.current.add(docSnap.id);
        updateDoc(doc(db, "calls", docSnap.id), {
          status: "ringing",
          ringingAt: serverTimestamp(),
        }).catch((err) => {
          console.warn(
            "[IncomingCallManager] Failed to mark call ringing:",
            err
          );
        });
      });
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Auto-stop ringing after timeout to avoid endless loops
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!activeCall) {
      // Reset per-call log guard when there is no active ringing call
      readyMissingLoggedRef.current = false;
      return;
    }

    if (activeCall.status !== "ringing") {
      return;
    }

    const ringingAtMs =
      (typeof activeCall.ringingAt?.toMillis === "function"
        ? activeCall.ringingAt.toMillis()
        : 0) || 0;
    const baseMs =
      ringingAtMs ||
      (typeof activeCall.createdAt?.toMillis === "function"
        ? activeCall.createdAt.toMillis()
        : 0) ||
      0;
    const elapsed = baseMs ? Date.now() - baseMs : 0;
    const remaining = Math.max(0, 30000 - elapsed);

    const endStaleCall = async () => {
      try {
        await updateDoc(doc(db, "calls", activeCall.id), { status: "no_answer" });
      } catch (err) {
        console.error("Error auto-ending stale ringing call:", err);
      } finally {
        setActiveCall(null);
      }
    };

    if (remaining === 0) {
      endStaleCall();
      return;
    }

    timeoutRef.current = setTimeout(endStaleCall, remaining);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [activeCall, appVisibility, callState, inCall]);

  // Optional local notification on native when ringing starts
  useEffect(() => {
    // ⏭️ Skip local notifications on iOS (CallKit handles this)
    if (!activeCall || !Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios') return;
    if (typeof window !== "undefined" && window.__telecomEnabled) return;
    if (appVisibility && appVisibility !== "foreground") return;

    const notify = async () => {
      let canDisplay = true;

      // Avoid local notifications for second calls while already in a call
      if (callState && callState !== "idle") return;
      if (inCall) return;

      if (!notificationPermissionsCheckedRef.current) {
        const permissions = await LocalNotifications.checkPermissions();
        if (permissions.display !== "granted") {
          const granted = await LocalNotifications.requestPermissions();
          if (granted.display !== "granted") {
            canDisplay = false;
          }
        }
        notificationPermissionsCheckedRef.current = true;
      }

      if (!canDisplay) return;

      // Avoid spamming notifications for the same call
      if (lastNotificationIdRef.current === activeCall.id) return;

      const id = Date.now() % 2147483647;
      lastNotificationIdRef.current = activeCall.id;

      const callerAgency =
        activeCall.fromAgency || activeCall.agency || "Unknown Agency";
      const callerRank = activeCall.fromRank || "";
      const callerName =
        activeCall.fromUserName ? activeCall.fromUserName : "Unknown caller";
      const callerLabel = callerRank
        ? `${callerRank} ${callerName}`
        : callerName;
      await LocalNotifications.schedule({
        notifications: [
          {
            id,
            title: "Incoming Call",
            body:
              callerLabel ? `${callerLabel} (${callerAgency})` : "Unknown caller",
            sound: "default",
            extra: { callId: activeCall.id, from: activeCall.from },
            actionTypeId: "INCOMING_CALL",
          },
        ],
      });
    };

    notify();
  }, [activeCall]);

  // Play ringtone + vibration while the in-app incoming modal is visible
  useEffect(() => {
    const isIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
    const shouldRing =
      !!activeCall &&
      !suppressOverlay &&
      !isIos &&
      (!appVisibility || appVisibility === "foreground");

    let cancelled = false;

    const ring = async () => {
      if (!shouldRing) {
        stopIncomingCallAlert();
        return;
      }

      if (
        Capacitor.isNativePlatform() &&
        Capacitor.getPlatform() === "android" &&
        activeCall?.id
      ) {
        if (audioDecisionCallIdRef.current !== activeCall.id) {
          const audioState = await getAndroidAudioState();
          if (cancelled) return;
          if (audioState) {
            const filterAllows = audioState.interruptionFilter !== "none";
            if (!filterAllows) {
              audioDecisionRef.current = { playSound: false, vibrate: false };
            } else if (audioState.ringerMode === "vibrate") {
              audioDecisionRef.current = { playSound: false, vibrate: false };
            } else if (audioState.ringerMode === "normal") {
              audioDecisionRef.current = { playSound: true, vibrate: false };
            } else {
              audioDecisionRef.current = { playSound: false, vibrate: false };
            }
          } else {
            audioDecisionRef.current = { playSound: true, vibrate: false };
          }
          audioDecisionCallIdRef.current = activeCall.id;
        }
      } else {
        audioDecisionRef.current = null;
        audioDecisionCallIdRef.current = null;
      }

      startIncomingCallAlert(audioDecisionRef.current || undefined);
    };

    ring();

    return () => {
      cancelled = true;
      stopIncomingCallAlert();
    };
  }, [activeCall?.id, suppressOverlay, appVisibility]);

  const handleDecline = async () => {
    if (!activeCall) return;
    try {
      await updateDoc(doc(db, "calls", activeCall.id), {
        status: "ended",
        reason: "declined",
        endedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error declining call:", err);
    } finally {
      setActiveCall(null);
    }
  };

  const handleAccept = async () => {
    if (!activeCall || !currentUser?.uid) return;
    try {
      const conversationId = await getOrCreateDirectConversation(
        currentUser.uid,
        activeCall.from
      );

      navigate(`/direct/${conversationId}`, {
        state: {
          autoAcceptCallId: activeCall.id,
          incomingCallPrefetch: activeCall,
        },
        replace: replaceCallNavigation,
      });

      setActiveCall(null);
    } catch (err) {
      console.error("Error accepting call:", err);
    }
  };

  // UI overlay for incoming calls is now handled by GlobalIncomingCallOverlay.
  // This manager remains active for notifications and auto-ending stale calls only.
  return null;
}
